<template>
  <div v-if="center">
    <gmap-map
      ref="map"
      :center="center"
      :zoom="zoom"
      :class="mapClasses"
      qaid="store-locator__map__gmap"
      @center_changed="centerChanged"
      @zoom_changed="zoomChanged"
    >
      <gmap-marker
        v-for="(store, index) in stores"
        :key="index"
        :ref="`marker${index}`"
        :position="storePos(store)"
        :draggable="false"
        :clickable="true"
        :icon="{
          url:
            MarkerUrl[favIndex == index ? 1 : 0][
              hoverIndex == index || cardHoverId == index || quickViewId == store.id ? 1 : 0
            ],
        }"
        :qaid="`store-locator__map__gmap__marker${index}`"
        @click="markerClicked(store.id, store.distance)"
        @mouseover="hoverIndex = index"
        @mouseout="hoverIndex = -1"
      ></gmap-marker>
    </gmap-map>

    <div v-if="showDebug" class="mt-3 text-sm text-secondary">{{ center.lat }}, {{ center.lng }}</div>
  </div>
</template>

<script>
// Useful reference links for vue-google-maps
// https://xkjyeah.github.io/vue-google-maps/autoapi.html
// https://github.com/Jeson-gk/vue2-google-maps/blob/master/API.md
import debounce from 'lodash/debounce';
import {Map as gmapMap} from 'vue2-google-maps';
import {Marker as gmapMarker} from 'vue2-google-maps';

const centerChangedDebounceTime = 250;

export default {
  components: {gmapMap, gmapMarker},

  props: {
    cardHoverId: {
      type: Number,
      default: 0,
    },
    quickViewId: {
      type: String,
      default: null,
    },
    stores: {
      type: Array,
      default: () => [],
    },
    centerCoords: {
      type: Object,
      default: () => {},
    },
    zoomLevel: {
      type: Number,
      default: 0,
    },
    boundsToSet: {
      type: Object,
      default: () => {},
    },
    boundsToSetByCenterAndZoom: {
      type: Object,
      default: () => {},
    },
    boundsToSetByTextQuery: {
      type: String,
      default: null,
    },
    mapClasses: {
      type: String,
      default: null,
    },
    showDebug: Boolean,
    isLoading: Boolean,
  },

  data() {
    return {
      center: {...this.centerCoords},
      zoom: this.zoomLevel,
      geocoder: null,
      hoverIndex: -1,
      MarkerUrl: [
        // [fav?][hover?]
        [
          `${ACC.config.azureCdnImagesUrl}/markers/normal/shadow/pin__normal.svg`,
          `${ACC.config.azureCdnImagesUrl}/markers/active/shadow/pin__normal.svg`,
        ],
        [
          `${ACC.config.azureCdnImagesUrl}/markers/normal/shadow/pin__normal--favorite.svg`,
          `${ACC.config.azureCdnImagesUrl}/markers/active/shadow/pin__active--favorite.svg`,
        ],
      ],
      isSettingBounds: false,
    };
  },

  computed: {
    favIndex() {
      for (let index = 0; index < this.stores.length; index++) {
        if (this.stores[index].isMyStore) return index;
      }
      return -1;
    },
  },

  mounted() {
    // These watchers have to be set up after map is initialized
    this.$refs.map.$mapPromise.then(() => {
      this.$refs.map.$once('bounds_changed', () => {
        this.$watch('quickViewId', (val) => {
          if (val) {
            this.extendBoundsForStore(val);
          }
        });
        this.$watch('boundsToSet', (val) => {
          if (val) {
            this.setBounds(val);
          }
        });
        this.$watch('boundsToSetByCenterAndZoom', (val) => {
          if (val) {
            this.setBoundsByCenterAndZoom(val);
          }
        });
        this.$watch('boundsToSetByTextQuery', (val) => {
          if (val) {
            this.findLocation(val);
          }
        });
        this.$watch('hoverIndex', (val) => {
          if (val) {
            this.$emit('pin-hover', val);
          }
        });

        // If these values have been provided by LocatorContainer before map has initialized, act on them now
        if (this.quickViewId) {
          this.extendBoundsForStore(this.quickViewId);
        } else if (this.boundsToSet) {
          this.setBounds(this.boundsToSet);
        } else if (this.boundsToSetByCenterAndZoom) {
          this.setBoundsByCenterAndZoom(this.boundsToSetByCenterAndZoom);
        } else if (this.boundsToSetByTextQuery) {
          this.findLocation(this.boundsToSetByTextQuery);
        }
      });
    });
  },

  methods: {
    storePos(store) {
      return {lat: store.location.latitude, lng: store.location.longitude};
    },
    markerClicked(id, distance) {
      this.$emit('selected-marker', id); // emit the clicked marker for parent to highlight it
      this.$emit('store-clicked', id, distance);
    },

    zoomChanged(val) {
      if (val) {
        if (!this.isSettingBounds) {
          this.handleUserChange();
        } else {
          this.emitNewCoords();
        }
      }
    },

    centerChanged: debounce(function() {
      // Used for map panning via drag or keyboard
      if (!this.isSettingBounds) {
        this.handleUserChange();
      } else {
        this.emitNewCoords();
      }
    }, centerChangedDebounceTime),

    setCenter(coords) {
      const currentCenter = this.$refs.map.$mapObject.getCenter().toJSON();
      if (
        coords &&
        Number.isFinite(coords.lat) &&
        Number.isFinite(coords.lng) &&
        !(coords.lat === currentCenter.lat && coords.lng === currentCenter.lng)
      ) {
        this.center = {...coords};
        this.$refs.map.$mapObject.setCenter(coords); // Overcome gmap-map component's reactivity limitations
      }
    },

    setZoom(zoom) {
      const currentZoom = this.$refs.map.$mapObject.getZoom();
      if (currentZoom !== zoom) {
        this.zoom = zoom;
        this.$refs.map.$mapObject.setZoom(zoom); // Overcome gmap-map component's reactivity limitations
      }
    },

    setBounds(bounds) {
      const mapObject = this.$refs.map.$mapObject;
      if (mapObject) {
        this.setIsSettingBounds(true);
        let currentBounds = mapObject.getBounds() ? mapObject.getBounds().toJSON() : {};
        if (
          !(
            bounds.east === currentBounds.east &&
            bounds.north === currentBounds.north &&
            bounds.south === currentBounds.south &&
            bounds.west === currentBounds.west
          )
        ) {
          // Pass a LatLngBoundsLiteral
          // https://developers.google.com/maps/documentation/javascript/reference/coordinates#LatLngBoundsLiteral
          mapObject.fitBounds(bounds);
        }
        this.setIsSettingBounds(false);
      }
    },

    setBoundsByCenterAndZoom(payload) {
      const currentCenter = this.$refs.map.$mapObject.getCenter().toJSON();
      const currentZoom = this.$refs.map.$mapObject.getZoom();
      // If we aren't changing centerCoords or zoom, then just do extendBoundsForStore
      if (
        currentCenter.lat === payload.centerCoords.lat &&
        currentCenter.lng === payload.centerCoords.lng &&
        currentZoom === payload.zoom
      ) {
        if (payload.extendBoundsForStore) {
          this.setIsSettingBounds(true);
          this.extendBoundsForStore(payload.extendBoundsForStore);
          this.setIsSettingBounds(false);
        }

        return;
      }

      this.setIsSettingBounds(true);

      if (payload.extendBoundsForStore) {
        // Make sure store is visible on the map by extending the map bounds if necessary
        this.$refs.map.$once('bounds_changed', () => {
          this.extendBoundsForStore(payload.extendBoundsForStore);
          this.setIsSettingBounds(false);
        });
      }

      this.setCenter(payload.centerCoords);
      this.setZoom(payload.zoom);
      this.emitNewCoords();

      if (!payload.extendBoundsForStore) {
        this.setIsSettingBounds(false);
      }
    },

    extendBoundsForStore(storeId) {
      let store = this.stores.find((store) => {
        return store.id === storeId;
      });
      if (store && store.location) {
        let storeLocation = {
          lat: store.location.latitude,
          lng: store.location.longitude,
        };
        let currentBounds = this.$refs.map.$mapObject.getBounds();
        if (!currentBounds.contains(storeLocation)) {
          let expandedBounds = currentBounds.extend(storeLocation);
          this.setBounds({
            south: expandedBounds.getSouthWest().lat(),
            west: expandedBounds.getSouthWest().lng(),
            north: expandedBounds.getNorthEast().lat(),
            east: expandedBounds.getNorthEast().lng(),
          });
        }
      }
    },

    findLocation(address) {
      this.setIsSettingBounds(true);
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({address: address}, (results, status) => {
        if (status === window.google.maps.GeocoderStatus.OK) {
          this.isSettingBounds(true);
          this.setCenter({lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng()});
          this.setZoom(this.initialZoom);
          this.isSettingBounds(false);
        } else {
          console.error('Geocode was not successful for the following reason: ' + status);
        }
        this.setIsSettingBounds(false);
      });
    },

    emitNewCoords() {
      this.zoom = this.$refs.map.$mapObject.getZoom();
      this.center = this.$refs.map.$mapObject.getCenter().toJSON();
      this.$emit('new-coords', this.center, this.zoom);
    },

    handleUserChange() {
      this.zoom = this.$refs.map.$mapObject.getZoom();
      this.center = this.$refs.map.$mapObject.getCenter().toJSON();
      let bounds = this.$refs.map.$mapObject.getBounds().toJSON();
      let boundsArray = [
        {lat: bounds.south, lng: bounds.west},
        {lat: bounds.north, lng: bounds.east},
      ];
      this.$emit('user-changed-map', boundsArray);
      this.$emit('new-coords', this.center, this.zoom);
    },

    setIsSettingBounds(val) {
      if (val) {
        this.isSettingBounds = true;
      } else {
        this.$nextTick(() => {
          setTimeout(() => {
            this.isSettingBounds = false;
          }, centerChangedDebounceTime * 2);
        });
      }
    },
  },
};
</script>
